<template>
    <InfoBox variant="dark" title="Programmer options">
        <div class="flex flex-row items-center">
            <!-- PSU Buttons -->
            <div class="flex-1">
                <VButton 
                    v-if="psu === 0" 
                    variant="green" 
                    @click="psu_ext" 
                    class="ml-4"
                >
                    EXT
                </VButton>
                <VButton 
                    v-else 
                    variant="dark" 
                    @click="psu_ext" 
                    class="ml-4"
                >
                    EXT
                </VButton>

                <VButton 
                    v-if="psu === 1" 
                    variant="green" 
                    @click="psu_18" 
                    class="ml-4"
                >
                    1.8V
                </VButton>
                <VButton 
                    v-else 
                    variant="dark" 
                    @click="psu_18" 
                    class="ml-4"
                >
                    1.8V
                </VButton>

                <VButton 
                    v-if="psu === 2" 
                    variant="green" 
                    @click="psu_33" 
                    class="ml-4"
                >
                    3.3V
                </VButton>
                <VButton 
                    v-else 
                    variant="dark" 
                    @click="psu_33" 
                    class="ml-4"
                >
                    3.3V
                </VButton>

                <!-- Baudrate Select -->
                <select
                    id="baudrates"
                    class="ml-4 bg-dark bg-gray-50 px-5 py-2 rounded-lg"
                    v-model="selectedBaudrate"
                    @change="onBaudrateChange"
                >
                    <!-- Render the formattedBaudrates array -->
                    <option
                        v-for="(item, index) in formattedBaudrates"
                        :key="index"
                        :value="item.value"
                    >
                        {{ item.label }}
                    </option>
                </select>
                <input type="checkbox" class="ml-4" v-model="enableSniffer" @change="onEnableSnifferChange"/><span class="ml-1">Enable sniffer</span>
            </div>

            <!-- Voltage & Idle display -->
            <div class="flex-0 pr-4 align-end justify-end text-right">
                <p 
                  v-if="voltage && (voltage > 1.7)" 
                  class="align-end text-green"
                >
                    {{ voltage }} V
                </p>
                <p 
                  v-else-if="voltage && (voltage <= 1.7)" 
                  class="align-end text-red"
                >
                    {{ voltage }} V
                </p>

                <p v-if="pinState === 0" class="text-green">
                    <i class="bi bi-check-circle"></i>
                    SPI bus idle
                </p>
                <p v-if="pinState === 1" class="text-red">
                    <i class="bi bi-x-circle"></i>
                    SPI bus busy
                </p>
                <p v-if="pinState === 2" class="text-yellow">
                    <i class="bi bi-scooter"></i>
                    SPI bus driven
                    <button @click="resetLine" class="text-sm underline">(Stop driving)</button>
                </p>
            </div>
        </div>
    </InfoBox>
</template>

<script>
import VButton from "./VButton.vue";
import InfoBox from "./InfoBox.vue";

export default {
    components: {
        VButton,
        InfoBox
    },
    props: {
        adapter: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            psu: -1,
            voltage: null,
            readVoltageTimer: null,
            psuStateTimer: null,
            idle: true,

            /* Array of possible baud rates */
            baudRates: [500000, 1000000, 2000000, 3000000, 5000000, 10000000, 50000000],

            /* The currently selected baud rate */
            selectedBaudrate: 1000000, // default
            enableSniffer: true, // default
            pinState: 0
        };
    },
    computed: {
        /**
         * Generate a user-friendly label for each baud rate
         * E.g., "500 kHz", "1 MHz", "2 MHz", "5 MHz"
         */
        formattedBaudrates() {
            return this.baudRates.map((rate) => {
                // Decide whether to display in kHz or MHz
                if (rate >= 1000000) {
                    return {
                        value: rate,
                        label: `${rate / 1000000} MHz`,
                    };
                } else {
                    return {
                        value: rate,
                        label: `${rate / 1000} kHz`,
                    };
                }
            });
        },
    },
    mounted() {
        // Start a timer when the component is mounted
        this.updatePSUState();
        this.updateVoltage();
        this.readVoltageTimer = setInterval(this.updateVoltage, 1000);

        // This should never change on its own, but let’s still check
        this.psuStateTimer = setInterval(this.updatePSUState, 5000);

        this.selectedBaudrate = this.adapter.baudrate;
    },
    beforeUnmount() {
        // Clear timers when the component is unmounted
        if (this.readVoltageTimer) clearInterval(this.readVoltageTimer);
        if (this.psuStateTimer) clearInterval(this.psuStateTimer);
    },
    methods: {
        async updatePSUState() {
            const psu = await this.adapter.readPSU();
            this.psu = psu;
        },
        async resetLine() {
            await this.adapter.resetLine();
            await this.updateVoltage();
        },
        async updateVoltage() {
            if (this.adapter && this.adapter.readVoltage) {
                try {
                    const v = await this.adapter.readVoltage();
                    const convertedVoltage = (5 / 4096) * v;
                    this.voltage = convertedVoltage.toFixed(3); // Keep 3 decimal places
                    const pinState = await this.adapter.pinState();
                    this.pinState = pinState;
                } catch (error) {
                    console.error("Error reading voltage:", error);
                }
            }
        },
        async psu_ext() {
            await this.adapter.configurePSU(0);
            await this.updatePSUState();
            await this.updateVoltage();
        },
        async psu_18() {
            await this.adapter.configurePSU(1);
            await this.updatePSUState();
            await this.updateVoltage();
        },
        async psu_33() {
            await this.adapter.configurePSU(2);
            await this.updatePSUState();
            await this.updateVoltage();
        },

        /* Called each time the user changes the baud rate select box */
        async onBaudrateChange() {
            // Example usage: configure the adapter with the selected baud rate
            console.log("Selected baud rate:", this.selectedBaudrate);
            // If your adapter has a method like `configureBaudrate()`, call it here:
            // this.adapter.configureBaudrate(this.selectedBaudrate);
            await this.adapter.setBaudrate(this.selectedBaudrate);
            console.log("updated")
        },

        onEnableSnifferChange() {
            console.log("Change sniffer configuration:", this.enableSniffer);
            this.adapter.sniffer = this.enableSniffer;
        }
    },
};
</script>

<style scoped>
/* Your scoped styles here */
</style>
